import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Layout from './InvestorDealAnalyzer/components/Layout';
import NumField from './InvestorDealAnalyzer/components/NumField';
import HelpTip from './InvestorDealAnalyzer/components/HelpTip';
import { computeDealMetrics, fmtUSD, fmtPct, fmtMoic, fmtDscr } from './InvestorDealAnalyzer/lib/math';

const INITIAL = {
  purchase_price: 0,
  down_payment: 0,
  interest_rate: 7,
  loan_term_years: 30,
  monthly_rent: 0,
  other_monthly_income: 0,
  vacancy_rate: 5,
  property_taxes: 0,
  insurance: 0,
  hoa: 0,
  property_management: 0,
  maintenance_reserve: 0,
  utilities: 0,
  other_expenses: 0,
  appreciation_rate: 3,
  exit_cap_rate: 7,
};

const SCOPED_CSS = `
  .ida-root { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; color: #092825; }
  .ida-root .ida-num::-webkit-outer-spin-button,
  .ida-root .ida-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .ida-root .ida-num { -moz-appearance: textfield; }
`;

function Metric({ label, value, help, sign, color, testId }) {
  const klass =
    color === 'pos' ? 'text-[#2FA163]' :
    color === 'neg' ? 'text-red-600' :
    'text-[#092825]';
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {label}
        {help && <HelpTip text={help} />}
      </div>
      <div
        className={'text-2xl font-bold tabular-nums ' + klass}
        data-testid={testId}
      >
        {sign}{value}
      </div>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="text-xs uppercase tracking-wider text-[#286C4E] font-bold mt-4 mb-2 first:mt-0">
      {children}
    </div>
  );
}

export default function InvestorDealAnalyzerPage() {
  const [inputs, setInputs] = useState(INITIAL);
  const update = (patch) => setInputs((prev) => ({ ...prev, ...patch }));

  const r = useMemo(() => computeDealMetrics(inputs), [inputs]);
  const active = inputs.purchase_price > 0;
  const cfColor = r.monthlyCashFlow > 0 ? 'pos' : r.monthlyCashFlow < 0 ? 'neg' : null;

  return (
    <Layout>
      <Helmet>
        <title>Free Investor Deal Analyzer · Cash Flow, Cap Rate, DSCR · I Need Numbers</title>
        <meta
          name="description"
          content="Free investor deal analyzer for real estate agents. Run cash flow, cap rate, cash-on-cash, DSCR, and 5-year ROI on any investment property. No login."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <style>{SCOPED_CSS}</style>

      <div className="ida-root" data-testid="ida-root">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
          <div className="text-xs uppercase tracking-wider text-[#2FA163] font-semibold mb-2">
            Free tool · I Need Numbers
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#092825] mb-2">
            Investor Deal Analyzer
          </h1>
          <p className="text-slate-700 max-w-2xl">
            Run the numbers on any investment property — cash flow, cap rate,
            cash-on-cash, DSCR, and a 5-year projection. Every metric updates
            live as you type. Nothing saved, nothing sent.
          </p>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            <div className="lg:col-span-3" data-testid="ida-inputs">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#092825] mb-4">Purchase &amp; financing</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumField label="Purchase price" prefix="$"
                    value={inputs.purchase_price}
                    onChange={(v) => update({ purchase_price: v })}
                    help="The price you'd pay for the property today."
                    testId="ida-input-purchase-price" />
                  <NumField label="Down payment" prefix="$"
                    value={inputs.down_payment}
                    onChange={(v) => update({ down_payment: v })}
                    help="The cash you'd put in at close. Typical investor down payments are 20\u201325% of purchase price."
                    testId="ida-input-down-payment" />
                  <NumField label="Interest rate" suffix="%"
                    value={inputs.interest_rate}
                    onChange={(v) => update({ interest_rate: v })}
                    help="Your expected mortgage rate as an annual percentage. Investor loans typically run 0.5\u20131% higher than owner-occupied rates."
                    testId="ida-input-interest-rate" />
                  <NumField label="Loan term (years)"
                    value={inputs.loan_term_years}
                    onChange={(v) => update({ loan_term_years: v })}
                    help="How many years the loan amortizes over. 30 years is standard for residential investment property."
                    testId="ida-input-loan-term" />
                </div>

                <h2 className="text-lg font-bold text-[#092825] mt-8 mb-4">Income (monthly)</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumField label="Monthly rent" prefix="$"
                    value={inputs.monthly_rent}
                    onChange={(v) => update({ monthly_rent: v })}
                    help="Total monthly rent collected at 100% occupancy."
                    testId="ida-input-monthly-rent" />
                  <NumField label="Other monthly income" prefix="$"
                    value={inputs.other_monthly_income}
                    onChange={(v) => update({ other_monthly_income: v })}
                    help="Pet rent, parking, laundry, storage, utility reimbursements — any revenue beyond the base rent."
                    testId="ida-input-other-income" />
                  <NumField label="Vacancy rate" suffix="%"
                    value={inputs.vacancy_rate}
                    onChange={(v) => update({ vacancy_rate: v })}
                    help="Expected percentage of the year the unit sits empty or rent-delinquent. 5% is a common assumption; use the local market figure if you have it."
                    testId="ida-input-vacancy-rate" />
                </div>

                <h2 className="text-lg font-bold text-[#092825] mt-8 mb-4">Operating expenses (monthly)</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumField label="Property taxes" prefix="$"
                    value={inputs.property_taxes}
                    onChange={(v) => update({ property_taxes: v })}
                    help="Monthly share of annual property taxes. Divide the annual bill by 12."
                    testId="ida-input-property-taxes" />
                  <NumField label="Insurance" prefix="$"
                    value={inputs.insurance}
                    onChange={(v) => update({ insurance: v })}
                    help="Monthly cost of landlord / dwelling insurance. Often 0.25\u20130.5% of purchase price per year."
                    testId="ida-input-insurance" />
                  <NumField label="HOA fees" prefix="$"
                    value={inputs.hoa}
                    onChange={(v) => update({ hoa: v })}
                    help="Monthly HOA or condo association dues, if any."
                    testId="ida-input-hoa" />
                  <NumField label="Property management" prefix="$"
                    value={inputs.property_management}
                    onChange={(v) => update({ property_management: v })}
                    help="Monthly management fee. Typically 8\u201310% of gross rent for single-family; varies by market."
                    testId="ida-input-property-management" />
                  <NumField label="Maintenance reserve" prefix="$"
                    value={inputs.maintenance_reserve}
                    onChange={(v) => update({ maintenance_reserve: v })}
                    help="Monthly set-aside for repairs, turnover, and capital expenses. 8\u201315% of rent is typical."
                    testId="ida-input-maintenance" />
                  <NumField label="Utilities (landlord-paid)" prefix="$"
                    value={inputs.utilities}
                    onChange={(v) => update({ utilities: v })}
                    help="Any utilities the landlord pays rather than the tenant \u2014 typically water / sewer / trash in multifamily."
                    testId="ida-input-utilities" />
                  <NumField label="Other monthly expenses" prefix="$"
                    value={inputs.other_expenses}
                    onChange={(v) => update({ other_expenses: v })}
                    help="Anything else: lawn care, pest control, snow removal, accounting, etc."
                    testId="ida-input-other-expenses" />
                </div>

                <h2 className="text-lg font-bold text-[#092825] mt-8 mb-4">Projection assumptions</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumField label="Annual appreciation rate" suffix="%"
                    value={inputs.appreciation_rate}
                    onChange={(v) => update({ appreciation_rate: v })}
                    help="Expected year-over-year property value growth. 3% is a conservative long-term U.S. average; use your market's figure if you have it."
                    testId="ida-input-appreciation" />
                  <NumField label="Exit cap rate" suffix="%"
                    value={inputs.exit_cap_rate}
                    onChange={(v) => update({ exit_cap_rate: v })}
                    help="The cap rate you assume when selling in year 5. Higher exit cap = lower exit value; usually you should assume it's slightly higher than the entry cap rate."
                    testId="ida-input-exit-cap" />
                </div>
              </div>
            </div>

            <aside className="lg:col-span-2">
              <div className="lg:sticky lg:top-24 flex flex-col gap-5">
                <div
                  className="bg-white rounded-lg border border-slate-200 shadow-sm p-6"
                  data-testid="ida-metrics-panel"
                >
                  <h2 className="text-lg font-bold text-[#092825] mb-3">Deal metrics</h2>

                  <SectionHeader>Cash flow</SectionHeader>
                  <Metric
                    label="Monthly cash flow"
                    value={active ? fmtUSD(Math.round(r.monthlyCashFlow)) : '—'}
                    color={active ? cfColor : null}
                    sign={active && r.monthlyCashFlow > 0 ? '+' : ''}
                    help="Effective gross income minus operating expenses minus debt service, divided by 12. What you actually pocket (or pay out) each month."
                    testId="ida-metric-monthly-cash-flow"
                  />
                  <Metric
                    label="Annual cash flow"
                    value={active ? fmtUSD(Math.round(r.annualCashFlow)) : '—'}
                    color={active ? cfColor : null}
                    sign={active && r.annualCashFlow > 0 ? '+' : ''}
                    testId="ida-metric-annual-cash-flow"
                  />

                  <SectionHeader>Returns</SectionHeader>
                  <Metric
                    label="Cap rate"
                    value={active ? fmtPct(r.capRate) : '—'}
                    help="NOI divided by purchase price. The unleveraged yield — what the property earns before any mortgage. Most U.S. residential cap rates land in the 4\u20138% range."
                    testId="ida-metric-cap-rate"
                  />
                  <Metric
                    label="Cash-on-cash return"
                    value={active ? fmtPct(r.cashOnCash) : '—'}
                    color={active && r.cashOnCash < 0 ? 'neg' : null}
                    help="Annual cash flow divided by the cash you put in. The actual return on the money you invested — the metric investors care about most."
                    testId="ida-metric-cash-on-cash"
                  />
                  <Metric
                    label="DSCR"
                    value={active ? fmtDscr(r.dscr) : '—'}
                    color={active && r.dscr !== null && r.dscr < 1 ? 'neg' : null}
                    help="Debt service coverage ratio: NOI divided by annual debt service. Lenders want 1.2+. Below 1.0 means the property doesn't cover its own loan payments."
                    testId="ida-metric-dscr"
                  />
                  <Metric
                    label="Break-even occupancy"
                    value={active ? fmtPct(r.breakEvenOccupancy, 1) : '—'}
                    help="The minimum occupancy rate at which this deal breaks even (cash flow = 0). Lower is better. Above 95% means a single missed month puts you underwater."
                    testId="ida-metric-break-even"
                  />

                  <SectionHeader>5-year projection</SectionHeader>
                  <Metric
                    label="NOI (year 1)"
                    value={active ? fmtUSD(Math.round(r.noi)) : '—'}
                    help="Net Operating Income in year 1 \u2014 effective gross income minus operating expenses, before the mortgage."
                    testId="ida-metric-noi"
                  />
                  <Metric
                    label="Projected exit value (year 5)"
                    value={active ? fmtUSD(Math.round(r.exitValue)) : '—'}
                    testId="ida-metric-exit-value"
                  />
                  <Metric
                    label="Total 5-year ROI"
                    value={active ? fmtPct(r.totalROI5yr, 1) : '—'}
                    color={active && r.totalROI5yr < 0 ? 'neg' : null}
                    testId="ida-metric-total-roi"
                  />
                  <Metric
                    label="MOIC"
                    value={active ? fmtMoic(r.moic) : '—'}
                    help="Multiple on invested capital: total dollars returned divided by dollars invested over 5 years. 1.5x means you get back 1.5\u00d7 what you put in."
                    testId="ida-metric-moic"
                  />
                </div>

                {active && r.warnings.length > 0 && (
                  <div
                    className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-2"
                    data-testid="ida-warnings"
                  >
                    {r.warnings.map((w) => (
                      <div key={w.key} className="flex gap-2 items-start text-sm text-amber-900">
                        <span className="text-amber-600 font-bold shrink-0 mt-0.5" aria-hidden>⚠</span>
                        <span>{w.message}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-slate-100 border border-slate-200 rounded p-4 text-xs text-slate-600 leading-relaxed">
                  Estimates only. Real-world returns depend on rent achieved,
                  actual operating expenses, and market conditions. Always
                  verify rent comps and recent comparable sales before committing.
                </div>

                <div
                  className="bg-white rounded-lg border border-[#2FA163]/30 shadow-md p-8"
                  data-testid="ida-upgrade-cta"
                >
                  <div className="text-xs uppercase tracking-wider text-[#2FA163] font-semibold mb-2">
                    Want to share this deal with an investor client?
                  </div>
                  <h3 className="text-lg font-bold text-[#092825] mb-3">
                    Turn this into a branded investor report with a 7-day free trial.
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    On the paid I Need Numbers platform, every deal analysis saves
                    to your history, generates a polished branded PDF with your logo
                    and colors, and lets the Fairy AI Coach give deal-specific
                    feedback — so you can speak investor language with confidence.
                  </p>
                  <ul className="text-sm text-slate-700 space-y-1.5 mb-5">
                    <li className="flex gap-2"><span className="text-[#2FA163] font-bold">✓</span>Unlimited branded investor reports</li>
                    <li className="flex gap-2"><span className="text-[#2FA163] font-bold">✓</span>Deal history with notes and revisions</li>
                    <li className="flex gap-2"><span className="text-[#2FA163] font-bold">✓</span>AI Coach feedback on every deal</li>
                  </ul>
                  <Link
                    to="/pricing?source=investor-deal-analyzer&plan=pro"
                    data-testid="ida-trial-cta-btn"
                    className="inline-block w-full text-center text-white font-semibold px-6 py-3 rounded shadow transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #2FA163 0%, #286C4E 100%)' }}
                  >
                    Start my 7-day trial →
                  </Link>
                </div>
              </div>
            </aside>

          </div>
        </section>
      </div>
    </Layout>
  );
}
